import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { parseDateOfBirth } from '@application/parsers';
import type {
	InformationGatheringPatientIdentity,
	InformationGatheringRole,
} from '@feature/information-gathering/information-gathering.model';
import { CareNavigationService } from '@view/view_care_navigation/care_navigation.service';
import { useResolve, useResolveController } from '@visiba/instantiation';
import { NiN, NiNFormatter } from '@visiba/national-identity-number';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInformationGatheringNavigation } from '../use-information-gathering-navigation';
import { ViewRedRobinAnalyticActions, ViewRedRobinController } from '../view_red_robin.controller';

const DATE_OF_BIRTH_PLACEHOLDERS: Record<keyof NiN.Implementations, string> = {
	SE: 'YYYY-MM-DD',
	NO: 'DD.MM.YYYY',
	FI: 'DD.MM.YYYY',
	GB: 'DD/MM/YYYY',
	DK: 'DD.MM.YYYY',
	NL: 'DD-MM-YYYY',
};

export type RespondentType = 'me' | 'representative';

interface FormData {
	respondentType: RespondentType;
	patientNin: Nullable<string>;
	dateOfBirth: Nullable<string>;
}

export function useScreenWhoIsRespondant() {
	const controller = useResolveController(ViewRedRobinController);
	const analyticService = useResolve(AnalyticService);
	const careNavigationService = useResolve(CareNavigationService);
	const informationGatheringNavigation = useInformationGatheringNavigation();
	const flowData = controller.getFlowData();
	const country = flowData.currentCountry;
	const [showAbove110Dialog, setShowAbove110Dialog] = useState(false);
	const [patientNin, setPatientNin] = useState<Nullable<string>>(null);
	const [patientDateOfBirth, setPatientDateOfBirth] = useState('');
	const continueToRedRobin = useCallback(
		(role: InformationGatheringRole, patientIdentity: InformationGatheringPatientIdentity) => {
			void informationGatheringNavigation.start(role, 'red-robin', patientIdentity);
		},
		[informationGatheringNavigation],
	);

	const data = useMemo(
		() => ({
			ninPlaceholder: NiNFormatter.COUNTRIES[country] ?? '',
			dateOfBirthPlaceholder: DATE_OF_BIRTH_PLACEHOLDERS[country] ?? '',
			countryNinHasAgeSupport: ['SE', 'NO', 'FI'].includes(country),
			useNhsNumber: country === 'GB',
		}),
		[country],
	);

	useEffect(() => {
		analyticService.trackPageView({ customTitle: 'RedRobin: Who is respondent' });
	}, [analyticService]);

	const getDobFromNin = useCallback(
		(nin: string): Date | null => {
			const implementation = NiN.produce(country);
			return implementation.getDateOfBirth(implementation.normalize(nin));
		},
		[country],
	);

	const submit = useCallback(
		(form: FormData) => {
			if (form.respondentType === 'me') {
				continueToRedRobin('self', { dateOfBirth: '', nhsNumber: '' });
				return;
			}

			const dateOfBirth =
				!data.countryNinHasAgeSupport && form.dateOfBirth ? parseDateOfBirth(form.dateOfBirth) : getDobFromNin(form.patientNin ?? '');

			if (dateOfBirth === null) {
				return;
			}

			const formattedDateOfBirth = dayjs(dateOfBirth).format('YYYY-MM-DD');
			setPatientNin(form.patientNin);
			setPatientDateOfBirth(formattedDateOfBirth);
			const age = dayjs().diff(dayjs(dateOfBirth), 'years');

			if (age > 110) {
				setShowAbove110Dialog(true);
			} else if (age >= 1) {
				continueToRedRobin('representative', {
					nhsNumber: form.patientNin ?? '',
					dateOfBirth: data.countryNinHasAgeSupport ? '' : formattedDateOfBirth,
				});
			} else {
				analyticService.trackEvent(
					AnalyticCategories.RedRobin,
					ViewRedRobinAnalyticActions.PatientUnderOneYearOfAge,
					'Patient is under 1 year of age',
				);
				careNavigationService.setCurrentPatientDateOfBirth(formattedDateOfBirth);
				void informationGatheringNavigation.start('representative', 'care-navigation', {
					dateOfBirth: data.countryNinHasAgeSupport ? '' : formattedDateOfBirth,
					nhsNumber: form.patientNin ?? '',
				});
			}
		},
		[
			analyticService,
			careNavigationService,
			continueToRedRobin,
			data.countryNinHasAgeSupport,
			getDobFromNin,
			informationGatheringNavigation,
		],
	);

	return {
		data,
		submit,
		showAbove110Dialog,
		closeAbove110Dialog: () => setShowAbove110Dialog(false),
		continueToRedRobin,
		continueWithMessaging: () => {
			void informationGatheringNavigation.start('representative', 'messaging', {
				dateOfBirth: patientDateOfBirth,
				nhsNumber: patientNin ?? '',
			});
		},
		ninDateIsInThePast: (nin: string) => {
			if (!data.countryNinHasAgeSupport) {
				return true;
			}

			const dateOfBirth = getDobFromNin(nin);
			return dateOfBirth !== null && dayjs().isAfter(dateOfBirth);
		},
		validateDateOfBirth: (dateOfBirth: string) => {
			const parsed = parseDateOfBirth(dateOfBirth);
			return parsed !== null && dayjs().isAfter(parsed);
		},
	};
}
